import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api, runWithAuditReason } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, GridPager, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, ExportMenu } from '../../components/AppToolbar';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useReasonGate, useConfirm } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { BusyIcon, LoaderSymbol } from '../../components/loading';
import { fmtLocalDate } from '../../utils/datetime';
import { useModalKeys } from '../../hooks/useModalKeys';
import { AiCombo } from '../../components/AiCombo';
import { ViewToggle } from '../../components/ViewToggle';
import { QuickAddDialog } from '../modules/QuickAddDialog';
import type { CrudField } from '../modules/moduleCrud';

// ─────────────────────────────────────────────────────────────────────────────
// Types (mirror the API contracts in AlpacaIntelligence.Application.MorTemplate)
// ─────────────────────────────────────────────────────────────────────────────
interface TemplateRow {
  oid:          string;
  templateID:   string | null;
  templateName: string | null;
  reportName:   string | null;
  categoryName: string | null;
  frequency:    string | null;
  startDate:    string | null;
  endDate:      string | null;
  active:       boolean;
}
interface SiteRow {
  oid: string; siteId: string | null; siteName: string | null; description: string | null;
  address: string | null; keyMap: string | null; samplingAddress: string | null; pwsid: string | null;
  siteCode: string | null; siteNumber: string | null; sampleType: string | null;
}
interface ParamRow {
  oid: string; parameterName: string | null; methodName: string | null;
  synonym: string | null; matrix: string | null; test: string | null; components: string | null;
  rpdSigFig: string | null; rpdCutOff: string | null; rpdDecimal: string | null;
  recSigFig: string | null; recCutOff: string | null; recDecimal: string | null;
}
interface CategoryOpt { id: string; name: string; }
interface TemplateDetail {
  oid:          string;
  templateID:   string | null;
  templateName: string | null;
  reportName:   string | null;
  categoryId:   string | null;
  categoryName: string | null;
  frequency:    string | null;
  startDate:    string | null;
  endDate:      string | null;
  active:       boolean;
  retireDate:   string | null;
  emailTo:      string | null;
  comment:      string | null;
  sampleSites:  SiteRow[];
  parameters:   ParamRow[];
}
interface Draft {
  templateName: string;
  reportName:   string;
  categoryId:   string;
  frequency:    string;
  startDate:    string;   // yyyy-MM-dd
  endDate:      string;   // yyyy-MM-dd
  active:       boolean;
  retireDate:   string;   // yyyy-MM-dd
  emailTo:      string;
  comment:      string;
}
type Mode = 'view' | 'edit' | 'add';
type PickerKind = 'site' | 'param' | null;

const EMAIL_RE = /^[\w.+-]+@([\w-]+\.)+[\w-]{2,}$/;
const dateInput = (s?: string | null) => (s ? s.slice(0, 10) : '');
const emptyDraft = (): Draft => ({
  templateName: '', reportName: '', categoryId: '', frequency: '',
  startDate: '', endDate: '', active: true, retireDate: '', emailTo: '', comment: '',
});

// Welded Category "+" quick-add (owner request 2026-10-13, image 504) — legacy XAF
// grid-lookup New-button parity (DWQRReportTemplateSetup.Category is a plain ReportCategory
// lookup with no AllowNew=False). Creates a dbo.ReportCategory via its own master endpoint,
// then re-reads the MOR category list and selects the new row. No new backend.
const CATEGORY_QUICKADD: NonNullable<CrudField['quickAdd']> = {
  createUrl: '/api/v1/report-categories',
  title: 'Category',
  icon: 'ti-category',
  nameKey: 'category',
  welded: true,
  fields: [
    { key: 'category', label: 'Category', type: 'text', required: true, maxLength: 200, requiredMessage: 'Category must not be empty.' },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function MorTemplateSetupPage() {
  const notify  = useNotify();
  const runSave = useSaveFlow();
  const gateReason = useReasonGate();
  const { confirm } = useConfirm();

  const [rows,    setRows]    = useState<TemplateRow[]>([]);
  const [cats,    setCats]    = useState<CategoryOpt[]>([]);
  const [reportNames, setReportNames] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [search,  setSearch]  = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [loadMs,  setLoadMs]  = useState<number | null>(null);
  const [fullGrid, setFullGrid] = useState(false);   // page-level list-grid maximize (search-row zoom)
  const [catAddOpen, setCatAddOpen] = useState(false);   // Category "+" welded quick-add dialog

  // Full-page detail (add / edit / view) — replaces the list when open.
  const [open,    setOpen]    = useState(false);
  const [mode,    setMode]    = useState<Mode>('view');
  const [current, setCurrent] = useState<TemplateDetail | null>(null);
  const [draft,   setDraft]   = useState<Draft>(emptyDraft());
  const [sites,   setSites]   = useState<SiteRow[]>([]);
  const [params,  setParams]  = useState<ParamRow[]>([]);
  const [saving,  setSaving]  = useState(false);
  const [busy,    setBusy]    = useState(false);   // loading a detail record

  // Add picker (sample site / parameter lookup) — DEBOUNCED SERVER-SIDE type-ahead
  // (owner report 2026-10-16: the picker opened slowly and the search felt broken
  // because the whole ~9k/~600-row list was loaded into the browser and filtered on
  // every keystroke).  Now each search fetches at most 200 matching rows from SQL.
  const [picker,    setPicker]    = useState<PickerKind>(null);
  const [pickSearch, setPickSearch] = useState('');          // global search box
  const [pickLoad,  setPickLoad]  = useState(false);
  const [pickRows,  setPickRows]  = useState<(SiteRow | ParamRow)[]>([]);  // current server results
  // Rows ticked in the picker (by oid) — added together when the user clicks OK.
  const [pickSel,   setPickSel]   = useState<Set<string | number>>(new Set());
  // Accumulates the ROW objects behind pickSel so a selection survives changing the
  // search term (the grid only holds the CURRENT search's rows).
  const pickSelRows = useRef<Map<string | number, SiteRow | ParamRow>>(new Map());

  // Test action (legacy btn_DWQRTestData) — date-range popup
  const [testOpen, setTestOpen] = useState(false);
  const [testFrom, setTestFrom] = useState('');
  const [testTo,   setTestTo]   = useState('');
  const [testBusy, setTestBusy] = useState(false);

  // ── Fetch list + category lookups ───────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const [listRes, catRes, rnRes] = await Promise.all([
        api.get('/api/v1/mor-template'),
        api.get('/api/v1/mor-template/lookups/categories'),
        api.get('/api/v1/mor-template/lookups/report-names'),
      ]);
      setRows((listRes.data as { data: TemplateRow[] }).data ?? []);
      setCats((catRes.data as { data: CategoryOpt[] }).data ?? []);
      setReportNames((rnRes.data as { data: { value: string; label: string }[] }).data ?? []);
    } catch (e: unknown) {
      setError(apiErr(e, 'Failed to load MOR templates'));
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // Esc restores the maximized list grid (search-row Maximize button).
  useEffect(() => {
    if (!fullGrid) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullGrid(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullGrid]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const t = search.toLowerCase();
    return rows.filter(r =>
      [r.templateID, r.templateName, r.reportName, r.categoryName, r.frequency]
        .some(v => (v ?? '').toLowerCase().includes(t)));
  }, [rows, search]);

  const cols: AppColumn<TemplateRow>[] = [
    // Per-row Edit pencil next to the checkbox (owner request 2026-10-13) — opens the
    // record directly in edit mode, like Formula Library / SDMS Formula Setup.
    { field: 'edit', header: '', width: 40, sortable: false, filterable: false, noExport: true,
      render: r => (
        <button title="Edit" aria-label="Edit"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1F4E79', padding: 2 }}
          onClick={e => { e.preventDefault(); e.stopPropagation(); void openRowEdit(r); }}>
          <i className="ti ti-edit" style={{ fontSize: 16 }} />
        </button>
      ) },
    // No-space column captions for the highlighted columns (owner request 2026-10-15,
    // image 644 — legacy ALPACA list-view parity); Category / Frequency are single words.
    { field: 'templateID',   header: 'TemplateID',   width: 130 },
    { field: 'templateName', header: 'TemplateName', width: 240 },
    { field: 'reportName',   header: 'ReportName',   width: 220 },
    { field: 'categoryName', header: 'Category',      width: 150 },
    { field: 'frequency',    header: 'Frequency',     width: 130 },
    { field: 'startDate',    header: 'StartDate',     width: 130, type: 'date' },
    { field: 'endDate',      header: 'EndDate',       width: 130, type: 'date' },
    // IsActive column hidden on the list (owner request 2026-10-13); Active is still
    // edited on the detail form.
  ];

  // ── Detail open/close ────────────────────────────────────────────────────────
  const draftFromDetail = (d: TemplateDetail): Draft => ({
    templateName: d.templateName ?? '', reportName: d.reportName ?? '',
    categoryId: d.categoryId ?? '', frequency: d.frequency ?? '',
    startDate: dateInput(d.startDate), endDate: dateInput(d.endDate),
    active: d.active, retireDate: dateInput(d.retireDate),
    emailTo: d.emailTo ?? '', comment: d.comment ?? '',
  });
  const applyDetail = (d: TemplateDetail, m: Mode) => {
    setCurrent(d); setDraft(draftFromDetail(d));
    setSites(d.sampleSites ?? []); setParams(d.parameters ?? []); setMode(m);
  };

  async function loadDetail(oid: string): Promise<TemplateDetail | null> {
    try {
      const res = await api.get(`/api/v1/mor-template/${oid}`);
      return (res.data as { data: TemplateDetail }).data;
    } catch (e: unknown) {
      notify.error(apiErr(e, 'Could not load template detail.'));
      return null;
    }
  }

  async function openDetail(row: TemplateRow) {
    setBusy(true); setOpen(true); setMode('view'); setCurrent(null);
    const d = await loadDetail(row.oid);
    setBusy(false);
    if (!d) { setOpen(false); return; }
    applyDetail(d, 'view');
  }
  function openAdd() {
    setCurrent(null); setDraft(emptyDraft()); setSites([]); setParams([]);
    setMode('add'); setOpen(true);
  }
  async function openEditSelected() {
    if (selected.size !== 1) { notify.warning('Select exactly one row to edit.'); return; }
    const oid = String(Array.from(selected)[0]);
    setBusy(true); setOpen(true); setMode('edit'); setCurrent(null);
    const d = await loadDetail(oid);
    setBusy(false);
    if (!d) { setOpen(false); return; }
    applyDetail(d, 'edit');
  }
  // Edit pencil on a list row (owner request 2026-10-13) → open that record directly in EDIT mode.
  async function openRowEdit(row: TemplateRow) {
    setBusy(true); setOpen(true); setMode('edit'); setCurrent(null);
    const d = await loadDetail(row.oid);
    setBusy(false);
    if (!d) { setOpen(false); return; }
    applyDetail(d, 'edit');
  }
  // "Detail View" from the list toggle (owner request 2026-10-16) → open the selected
  // row (else the first) in VIEW mode, like the shared ModuleListView / Payment Terms.
  function toggleToDetail() {
    const sel = selected.size === 1 ? filtered.find((r) => r.oid === String(Array.from(selected)[0])) : undefined;
    const row = sel ?? filtered[0];
    if (row) void openDetail(row);
  }
  function closeDetail() {
    setOpen(false); setCurrent(null); setDraft(emptyDraft());
    setSites([]); setParams([]); setPicker(null); setPickSel(new Set()); setTestOpen(false);
  }

  const editable = mode === 'add' || mode === 'edit';

  // ── Child collections (link / remove) ───────────────────────────────────────
  // Bulk remove (ALPACA "Remove" toolbar button — removes the ticked child rows).
  function removeSites(oids: string[])  { setSites(s => s.filter(x => !oids.includes(x.oid))); }
  function removeParams(oids: string[]) { setParams(p => p.filter(x => !oids.includes(x.oid))); }

  function openPicker(kind: PickerKind) {
    setPicker(kind); setPickSearch(''); setPickSel(new Set()); setPickRows([]);
    pickSelRows.current = new Map();
  }
  // Debounced server-side type-ahead: fetch at most 200 matching rows from SQL
  // (API Clamp cap) instead of the whole ~9k/~600-row list — immediate on open,
  // 250ms while typing (owner report 2026-10-16).
  useEffect(() => {
    if (!picker) return;
    const kind = picker;
    const term = pickSearch.trim();
    let alive = true;
    const run = async () => {
      setPickLoad(true);
      try {
        const path = kind === 'site' ? 'sample-sites' : 'test-parameters';
        const res = await api.get(`/api/v1/mor-template/${path}`, {
          params: { search: term || undefined, limit: 200 },
        });
        const data = (res.data as { data: (SiteRow | ParamRow)[] }).data ?? [];
        if (alive) setPickRows(data);
      } catch (e: unknown) {
        if (alive) notify.error(apiErr(e, 'Failed to load list.'));
      } finally {
        if (alive) setPickLoad(false);
      }
    };
    const t = setTimeout(run, term ? 250 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [picker, pickSearch, notify]);

  function addFromPicker(row: SiteRow | ParamRow) {
    if (picker === 'site') {
      const s = row as SiteRow;
      setSites(prev => prev.some(x => x.oid === s.oid) ? prev : [...prev, s]);
    } else {
      const p = row as ParamRow;
      setParams(prev => prev.some(x => x.oid === p.oid) ? prev : [...prev, p]);
    }
  }
  // Grid tick → remember the row OBJECTS (across searches) so OK can add them all
  // even though the grid only holds the current search's rows.
  function onPickSelect(next: Set<string | number>) {
    const map = pickSelRows.current;
    next.forEach(id => {
      if (!map.has(id)) {
        const r = pickRows.find(x => x.oid === id);
        if (r) map.set(id, r);
      }
    });
    Array.from(map.keys()).forEach(id => { if (!next.has(id)) map.delete(id); });
    setPickSel(next);
  }
  // OK → add every ticked row at once (dedupes against already-linked rows).
  function addSelected() {
    pickSelRows.current.forEach(row => addFromPicker(row));
    setPicker(null); setPickSel(new Set()); pickSelRows.current = new Map();
  }

  // ── Test action (legacy btn_DWQRTestData) ───────────────────────────────────
  function openTest() {
    if (sites.length === 0 || params.length === 0) {
      notify.warning('Add at least one Sample Location and one Test Parameter before testing.');
      return;
    }
    setTestFrom(draft.startDate || ''); setTestTo(draft.endDate || ''); setTestOpen(true);
  }
  async function runTest() {
    // ALPACA's Test RENDERS the report (PDF preview), not a count — for the CURRENT
    // form selection (Report × sites × parameters × date range), no save required.
    if (!draft.reportName) { notify.warning('Select a Report before testing.'); return; }
    if (sites.length === 0 || params.length === 0) { notify.warning('Add at least one Sample Location and one Parameter.'); return; }
    if (!testFrom || !testTo) { notify.warning('Enter Date From and Date To.'); return; }
    if (testBusy) return;
    setTestBusy(true);
    try {
      const res = await api.post('/api/v1/mor-template/test-report', {
        reportName:    draft.reportName,
        sampleSiteIds: sites.map(s => s.oid),
        parameterIds:  params.map(p => p.oid),
        from: testFrom, to: testTo,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank');           // show the rendered report PDF
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setTestOpen(false);
    } catch (e: unknown) {
      notify.error(await blobErr(e, 'Test failed.'));
    } finally {
      setTestBusy(false);
    }
  }

  // ── Save / delete ───────────────────────────────────────────────────────────
  function validate(): string | null {
    if (!draft.templateName.trim()) return 'Template Name is required.';
    if (!draft.reportName.trim())   return 'Report Name is required.';
    if (draft.emailTo.trim()) {
      const bad = draft.emailTo.split(',').map(e => e.trim()).filter(e => e && !EMAIL_RE.test(e));
      if (bad.length) return `Invalid email format: ${bad.join(', ')}`;
    }
    return null;
  }
  function buildPayload() {
    return {
      templateName: draft.templateName.trim(),
      reportName:   draft.reportName.trim(),
      categoryId:   draft.categoryId || null,
      frequency:    draft.frequency.trim() || null,
      startDate:    draft.startDate || null,
      endDate:      draft.endDate || null,
      active:       draft.active,
      retireDate:   draft.retireDate || null,
      emailTo:      draft.emailTo.trim() || null,
      comment:      draft.comment.trim() || null,
      sampleSiteIds: sites.map(s => s.oid),
      parameterIds:  params.map(p => p.oid),
    };
  }
  /** Save the template.  closeAfter=true returns to the list (Save & Close);
   *  false keeps the form open on the saved record (Save). */
  async function handleSave(closeAfter: boolean) {
    const err = validate();
    if (err) { notify.warning(err); return; }
    if (saving) return;
    const editing = mode === 'edit' && current;
    setSaving(true);
    try {
      await runSave({
        // Reason-for-change gate (standing rule 11) — only on an edit (Create exempt).
        formName: 'MOR Template Setup',
        auditAction: editing ? 'edit' : 'create',
        save: async () => {
          let oid = current?.oid ?? '';
          if (editing) {
            await api.put(`/api/v1/mor-template/${current!.oid}`, buildPayload());
          } else {
            const res = await api.post('/api/v1/mor-template', buildPayload());
            oid = (res.data as { data: { id: string } }).data.id;
          }
          if (!closeAfter && oid) {
            const d = await loadDetail(oid);
            if (d) applyDetail(d, 'edit');
          }
        },
        successMessage: editing ? 'Template updated successfully.' : 'Template created successfully.',
        onSaved: async () => { if (closeAfter) { closeDetail(); setSelected(new Set()); await load(); } else { await load(); } },
        onError: (e) => notify.error(`Save failed: ${apiErr(e, 'Could not save template.')}`),
      });
    } finally {
      setSaving(false);
    }
  }
  async function handleDelete() {
    if (!current) return;
    if (!(await confirm({ title: 'Delete template?', message: `Delete template "${current.templateName}" (${current.templateID})?`, tone: 'danger', confirmLabel: 'Delete' }))) return;
    const reason = await gateReason('MOR Template Setup', 'delete');
    if (reason === null) return;
    if (saving) return;
    setSaving(true);
    try {
      await runWithAuditReason(reason, () => api.delete(`/api/v1/mor-template/${current.oid}`));
      closeDetail(); setSelected(new Set()); await load();
      notify.success('Template deleted.');
    } catch (e: unknown) {
      notify.error(`Delete failed: ${apiErr(e, 'Could not delete template.')}`);
    } finally {
      setSaving(false);
    }
  }
  async function deleteSelected() {
    if (selected.size === 0) { notify.warning('Select at least one row to delete.'); return; }
    const targets = rows.filter(r => selected.has(r.oid));
    if (!(await confirm({
      title: targets.length === 1 ? 'Delete template?' : 'Delete templates?',
      message: `Delete ${targets.length} template${targets.length === 1 ? '' : 's'}? (${targets.map(t => t.templateID).join(', ')})`,
      tone: 'danger', confirmLabel: 'Delete',
    }))) return;
    const reason = await gateReason('MOR Template Setup', 'delete');
    if (reason === null) return;
    if (saving) return;
    setSaving(true);
    try {
      await runWithAuditReason(reason, () => Promise.all(targets.map(t => api.delete(`/api/v1/mor-template/${t.oid}`))));
      setSelected(new Set()); await load();
      notify.success(`Deleted ${targets.length} template${targets.length === 1 ? '' : 's'}.`);
    } catch (e: unknown) {
      notify.error(`Delete failed: ${apiErr(e, 'Could not delete one or more templates.')}`);
      await load();
    } finally {
      setSaving(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Overlays that sit above the DETAIL view: the Add picker (sample site /
  // parameter lookup) and the Test date-range dialog.  The record detail itself
  // is a FULL-PAGE view (below) — never a popup.
  // ─────────────────────────────────────────────────────────────────────────────
  // Action bar — rendered at TOP and BOTTOM of the full-page detail (identical
  // set, BO UI Standard §4).  Save = solid 💾; Cancel/Close pushed right.
  const detailActionBar = (bottom?: boolean) => (
    <div className={`mt-actionbar${bottom ? ' bottom' : ''}`}>
      {editable ? (
        <>
          {/* Right-aligned (.mt-actionbar justify-content:flex-end), order
              Test · Save · Save & Close · Cancel (owner request 2026-10-15). */}
          <button className="ca-btn" onClick={openTest} disabled={busy} title={`Test (${formatCombo('alt+t')})`}><i className="ti ti-checkup-list" /> Test</button>
          <button className="ca-btn ca-btn-solid" onClick={() => handleSave(false)} disabled={saving || busy} title={`Save (${formatCombo('mod+s')})`}>
            {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
          </button>
          <button className="ca-btn" onClick={() => handleSave(true)} disabled={saving || busy} title={`Save & Close (${formatCombo('mod+enter')})`}>
            <span className="tb-i">💾</span> Save &amp; Close
          </button>
          <button className="ca-btn" onClick={closeDetail} disabled={saving}>
            <span className="tb-i">↩︎</span> Cancel
          </button>
        </>
      ) : (
        <>
          {/* Form-specific Test leads, then Edit · Delete · Close — right-aligned. */}
          <button className="ca-btn" onClick={openTest} disabled={busy} title={`Test (${formatCombo('alt+t')})`}><i className="ti ti-checkup-list" /> Test</button>
          <button className="ca-btn" onClick={() => setMode('edit')} disabled={busy}>
            <span className="tb-i tb-i-blue">✎</span> Edit
          </button>
          <button className="ca-btn" onClick={handleDelete} disabled={saving || busy}>
            <span className="tb-i tb-i-red">🗑</span> Delete
          </button>
          <button className="ca-btn" onClick={closeDetail}>
            <span className="tb-i">↩︎</span> Close
          </button>
        </>
      )}
    </div>
  );

  // Bespoke detail action-bar buttons → keyboard shortcuts via the ONE shared
  // hotkey layer (standing rule 14). The call is unconditional (rules of hooks);
  // the ARRAY is gated to when the full-page detail is open. Save / Save & Close
  // render only in edit/add mode; Test is available in both view and edit.
  useHotkeys(
    open
      ? [
          ...(editable
            ? [
                { combo: 'mod+s', label: 'Save', group: 'MOR Template Setup',
                  run: () => void handleSave(false), disabled: saving || busy },
                { combo: 'mod+enter', label: 'Save & Close', group: 'MOR Template Setup',
                  run: () => void handleSave(true), disabled: saving || busy },
              ]
            : []),
          { combo: 'alt+t', label: 'Test', group: 'MOR Template Setup',
            run: () => openTest(), disabled: busy },
        ]
      : [],
  );

  // Bespoke Test date-range popup keyboard: Esc closes (standing rule 14).
  const testKeys = useModalKeys(testOpen, { onClose: () => setTestOpen(false) });

  // ─────────────────────────────────────────────────────────────────────────────
  // DETAIL  (New / Edit / View) — a FULL-PAGE view that REPLACES the list
  // (BO UI Standard §4, standing rule 12 — superseded the earlier right-sized
  // popup on 2026-07-01).  Bespoke layout (not the shared SettingsDetailPage)
  // because the Sample Locations / Parameters child grids and the Test action
  // don't fit the CrudField model; anatomy follows the standard: view-then-edit,
  // action bar top + bottom, Save solid / Cancel.
  // ─────────────────────────────────────────────────────────────────────────────
  if (open) {
    return (
      <>
        <style>{MT_CSS}</style>
        <div className="bl-page">
          <div className="bl-grid-area">
            {/* List / Detail toggle — Detail active; List returns to the grid (owner request 2026-10-16). */}
            <div className="mt-viewtoggle">
              <ViewToggle view="detail" onChange={(v) => { if (v === 'list') closeDetail(); }} />
            </div>
            <div className="bl-card bl-card-flex">
              <div className="section-hdr mt-detail-hdr">
                {/* Navigator breadcrumb instead of the Audit chip (owner request 2026-10-16);
                    the Audit log stays on the list view's FormTitle. */}
                <div className="mt-detail-nav">
                  <FormTitle pageId="mor-template" title={`MOR Template Setup — ${mode === 'add' ? 'New' : (current?.templateID ?? '')}`} />
                  <div className="dp-breadcrumb mt-detail-bc">
                    <span>Home</span><i className="ti ti-chevron-right" />
                    <span>Settings</span><i className="ti ti-chevron-right" />
                    <span>Reporting</span><i className="ti ti-chevron-right" />
                    <a className="mt-bc-link" onClick={closeDetail}>MOR Template Setup</a><i className="ti ti-chevron-right" />
                    <span className="dp-bc-active">{mode === 'add' ? 'New' : (current?.templateName || current?.templateID || '')}</span>
                  </div>
                </div>
              </div>
              {detailActionBar()}

              <div className="mt-detail-body">
                {busy ? (
                  <div className="mt-busy"><LoaderSymbol size={14} /> Loading…</div>
                ) : (
                  <>
                    <div className="mt-section-title">General Information</div>
                    {/* 2-column side-label layout matching the legacy ALPACA MOR Template
                        Setup detail (owner request 2026-10-02). Row-major DOM order
                        interleaves the two columns so the layout reads:
                          LEFT              | RIGHT
                          Template ID       | Frequency
                          Template Name*    | Start Date
                          Report Name*      | End Date
                          Category          | Email To
                          Active            | Comment
                          Retire Date       | */}
                    <div className="mt-grid">
                      <Field label="Template ID">
                        <div className="mt-val">{current?.templateID || 'Autogenerate'}</div>
                      </Field>
                      <Field label="Frequency">
                        {editable
                          ? <input value={draft.frequency} onChange={e => setDraft(d => ({ ...d, frequency: e.target.value }))} />
                          : <div className="mt-val">{current?.frequency || '—'}</div>}
                      </Field>

                      <Field label="Template Name" required>
                        {editable
                          ? <input autoFocus value={draft.templateName} onChange={e => setDraft(d => ({ ...d, templateName: e.target.value }))} />
                          : <div className="mt-val"><strong>{current?.templateName || '—'}</strong></div>}
                      </Field>
                      <Field label="Start Date">
                        {editable
                          ? <input type="date" value={draft.startDate} onChange={e => setDraft(d => ({ ...d, startDate: e.target.value }))} />
                          : <div className="mt-val">{current?.startDate ? fmtLocalDate(current.startDate) : '—'}</div>}
                      </Field>

                      <Field label="Report Name" required>
                        {editable
                          ? (
                            <select value={draft.reportName} onChange={e => setDraft(d => ({ ...d, reportName: e.target.value }))}>
                              <option value="">— Select —</option>
                              {/* Keep an already-saved value visible even if its
                                  package/report was since retired. */}
                              {draft.reportName && !reportNames.some(r => r.value === draft.reportName) && (
                                <option value={draft.reportName}>{draft.reportName}</option>
                              )}
                              {reportNames.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                          )
                          : <div className="mt-val">{current?.reportName || '—'}</div>}
                      </Field>
                      <Field label="End Date">
                        {editable
                          ? <input type="date" value={draft.endDate} onChange={e => setDraft(d => ({ ...d, endDate: e.target.value }))} />
                          : <div className="mt-val">{current?.endDate ? fmtLocalDate(current.endDate) : '—'}</div>}
                      </Field>

                      <Field label="Category">
                        {editable
                          ? (
                            <>
                              {/* Welded ＋ Category combo ([＋][▼]) — legacy XAF grid-lookup
                                  New-button parity (owner request 2026-10-13, image 504). */}
                              <AiCombo
                                className="mt-cat-combo"
                                value={draft.categoryId || null}
                                options={cats.map(c => ({ value: c.id, label: c.name }))}
                                onChange={v => setDraft(d => ({ ...d, categoryId: v ?? '' }))}
                                onNew={() => setCatAddOpen(true)}
                                newTitle="Add Category"
                                placeholder="— None —"
                                naLabel="— None —"
                                compact
                              />
                              {catAddOpen && (
                                <QuickAddDialog
                                  spec={CATEGORY_QUICKADD}
                                  optionsUrl="/api/v1/mor-template/lookups/categories"
                                  onClose={() => setCatAddOpen(false)}
                                  onAdded={(opts, selectedId) => {
                                    setCats(opts as CategoryOpt[]);
                                    if (selectedId) setDraft(d => ({ ...d, categoryId: selectedId }));
                                  }}
                                />
                              )}
                            </>
                          )
                          : <div className="mt-val">{current?.categoryName || '—'}</div>}
                      </Field>
                      <Field label="Email To">
                        {editable
                          ? <input value={draft.emailTo} onChange={e => setDraft(d => ({ ...d, emailTo: e.target.value }))} />
                          : <div className="mt-val">{current?.emailTo || '—'}</div>}
                      </Field>

                      {/* NO left "Active" label in EITHER mode — the checkbox carries its own
                          "Active"/"Retired" caption (owner requests 2026-10-13, images 511 + 514).
                          View mode shows the SAME checkbox, just disabled (read-only). */}
                      <Field label="">
                        {editable
                          ? (
                            <label className="mt-check">
                              <input type="checkbox" checked={draft.active} onChange={e => setDraft(d => ({ ...d, active: e.target.checked }))} />
                              <span>{draft.active ? 'Active' : 'Retired'}</span>
                            </label>
                          )
                          : (
                            <label className="mt-check">
                              <input type="checkbox" checked={!!current?.active} disabled />
                              <span>{current?.active ? 'Active' : 'Retired'}</span>
                            </label>
                          )}
                      </Field>
                      <Field label="Comment">
                        {editable
                          ? <input value={draft.comment} onChange={e => setDraft(d => ({ ...d, comment: e.target.value }))} />
                          : <div className="mt-val">{current?.comment || '—'}</div>}
                      </Field>

                      <Field label="Retire Date">
                        {editable
                          ? <input type="date" value={draft.retireDate} onChange={e => setDraft(d => ({ ...d, retireDate: e.target.value }))} />
                          : <div className="mt-val">{current?.retireDate ? fmtLocalDate(current.retireDate) : '—'}</div>}
                      </Field>
                    </div>

                    <div className="mt-children">
                      <ChildPanel
                        title="Sample Locations" count={sites.length}
                        editable={editable} onAdd={() => openPicker('site')} onRemoveMany={removeSites}
                        head={['SiteName', 'Description', 'Address1', 'Address2', 'SamplingAddress', 'PWSID', 'SiteCode', 'SiteNumber', 'SampleType']}
                        rows={sites.map(s => ({
                          key: s.oid,
                          cells: [s.siteName ?? '', s.description ?? '', s.address ?? '', s.keyMap ?? '', s.samplingAddress ?? '', s.pwsid ?? '', s.siteCode ?? '', s.siteNumber ?? s.siteId ?? '', s.sampleType ?? ''],
                        }))}
                        emptyText="No sample locations linked." />
                      <ChildPanel
                        title="Parameters" count={params.length}
                        editable={editable} onAdd={() => openPicker('param')} onRemoveMany={removeParams}
                        head={['Parameter', 'Synonym', 'Matrix', 'Test', 'Method', 'Components', 'RPDSigFig', 'RPDCutOff', 'RPDDecimal', 'RECSigFig', 'RECCutOff', 'RECDecimal']}
                        rows={params.map(p => ({
                          key: p.oid,
                          cells: [p.parameterName ?? '', p.synonym ?? '', p.matrix ?? '', p.test ?? '', p.methodName ?? '', p.components ?? '', p.rpdSigFig ?? '', p.rpdCutOff ?? '', p.rpdDecimal ?? '', p.recSigFig ?? '', p.recCutOff ?? '', p.recDecimal ?? ''],
                        }))}
                        emptyText="No parameters linked." />
                    </div>
                  </>
                )}
              </div>

              {detailActionBar(true)}
            </div>
          </div>
        </div>

        {picker && (
          <PickerOverlay
            picker={picker} rows={pickRows} pickSearch={pickSearch} pickLoad={pickLoad}
            onSearch={setPickSearch}
            selectedIds={pickSel} onSelectionChange={onPickSelect}
            onAddSelected={addSelected}
            onClose={() => { setPicker(null); setPickSel(new Set()); pickSelRows.current = new Map(); }} />
        )}

        {testOpen && (
          <div className="mt-overlay mt-overlay-2" onClick={e => { if (e.target === e.currentTarget) setTestOpen(false); }}>
            <div className="mt-test" {...testKeys}>
              <div className="mt-test-hdr"><span><i className="ti ti-checkup-list" /> {draft.templateName ? `${draft.templateName} - Date Range` : 'Date Range'}</span>
                <button className="mt-x" onClick={() => setTestOpen(false)}>×</button></div>
              <div className="mt-test-body">
                <div className="mt-test-row">
                  <label><span className="mt-test-lbl">Date From:</span><input type="date" value={testFrom} onChange={e => setTestFrom(e.target.value)} /></label>
                  <label><span className="mt-test-lbl">Date To:</span><input type="date" value={testTo} onChange={e => setTestTo(e.target.value)} /></label>
                </div>
              </div>
              <div className="mt-test-ftr">
                <button className="mt-btn mt-btn-primary" onClick={runTest} disabled={testBusy}>
                  {testBusy ? 'Testing…' : 'OK'}
                </button>
                <button className="mt-btn mt-btn-secondary" onClick={() => setTestOpen(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // LIST
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{MT_CSS}</style>
      <div className={`bl-page${fullGrid ? ' mt-fullgrid' : ''}`}>
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Settings</span><i className="ti ti-chevron-right" />
              <span>Reporting</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">MOR Template Setup</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6) ON the grey page,
                with the selection pill as a title-row chip. */}
            <div className="dp-title-row">
              {/* List / Detail toggle — List active (owner request 2026-10-16); Detail opens the selected/first record. */}
              <ViewToggle view="list" onChange={(v) => { if (v === 'detail') toggleToDetail(); }} detailDisabled={filtered.length === 0} />
              <FormTitle pageId="mor-template" loadMs={loadMs} loading={loading} auditFormName="MOR Template Setup" />
              {selected.size > 0 && (
                <span className="mt-sel-pill" style={{ marginLeft: 10 }}>
                  <i className="ti ti-circle-check" /> {selected.size} selected
                  <button className="mt-sel-clear" onClick={() => setSelected(new Set())} title="Clear selection">×</button>
                </span>
              )}
            </div>
            <div className="dp-subtitle">
              Reusable MOR / DWQR report templates · Source: BTLIMS <code>DWQRReportTemplateSetup</code>
            </div>
          </div>
        </div>

        <div className="bl-grid-area">
          {/* Toolbar on its OWN full-width row ON the grey page (BO UI Standard v2) —
              canonical order New · Edit · Delete · Refresh · Export to. */}
          <div className="mt-max-hide" style={{ margin: '0 0 8px' }}>
            <AppToolbar
              newLabel="New"
              onNew={openAdd}
              onEdit={openEditSelected}
              editDisabled={selected.size !== 1}
              onDelete={deleteSelected}
              deleteDisabled={selected.size === 0}
              onRefresh={load}
              refreshing={loading}
              exportData={{ filename: 'mor-templates', ...gridExportMatrix(filtered, cols) }}
            />
          </div>

          {/* Search + Export + Maximize on ONE row OUTSIDE the grid card (owner request
              2026-10-13, images 507/508) — the grid's own header tools are hidden, so the
              Export + Zoom controls are NOT a band inside the grid card. Maximize is a
              page-level flex fill (mt-fullgrid on .bl-page), so this row stays visible. */}
          <div className="list-search-row" style={fullGrid ? { padding: '4px 0 7px' } : undefined}>
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" placeholder="Search templates…" value={search} onChange={e => setSearch(e.target.value)} />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
            </div>
            {filtered.length !== rows.length && (
              <span style={{ fontSize: 12, color: 'var(--ai-muted)' }}>{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
            )}
            <div className="mt-grid-acts">
              <ExportMenu iconOnly triggerClassName="adg-max-btn" filename="mor-templates" {...gridExportMatrix(filtered, cols)} />
              <button type="button" className="adg-max-btn" onClick={() => setFullGrid(v => !v)}
                title={fullGrid ? 'Restore (Esc)' : 'Maximize'} aria-label={fullGrid ? 'Restore' : 'Maximize'}>
                <i className={`ti ti-arrows-${fullGrid ? 'minimize' : 'maximize'}`} />
              </button>
            </div>
          </div>

          {/* ONE white grid card — grid + its single footer only. */}
          <div className="bl-card bl-card-flex">
            {error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, var(--ai-orange))', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
              </div>
            ) : (
              <AppDataGrid<TemplateRow>
                rows={filtered}
                columns={cols}
                getRowId={r => r.oid}
                loading={loading}
                loadMs={loadMs}
                hideHeaderTools
                selectable
                selectedIds={selected}
                onSelectionChange={setSelected}
                onRowClick={openDetail}
                emptyText="No MOR templates found"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Picker grid columns — mirror the legacy ALPACA "Sample Sites" / parameter
// lookup grids.  (dbo.SampleSites has a single Address column in this DB.)
const SITE_PICKER_COLS: AppColumn<SiteRow>[] = [
  { field: 'siteName',        header: 'SiteName' },
  { field: 'description',     header: 'Description' },
  // Legacy picker: "Address1" = SampleSites.Address, "Address2" = SampleSites.KeyMap;
  // the PICKER has NO SampleType column (it lives on the child grid only).
  { field: 'address',         header: 'Address1' },
  { field: 'keyMap',          header: 'Address2' },
  { field: 'samplingAddress', header: 'SamplingAddress' },
  { field: 'pwsid',           header: 'PWSID' },
  { field: 'siteCode',        header: 'SiteCode' },
  { field: 'siteNumber',      header: 'SiteNumber' },
];
// Parameter PICKER shows only the legacy lookup columns (the RPD/REC columns
// live on the linked detail grid, not the picker).
const PARAM_PICKER_COLS: AppColumn<ParamRow>[] = [
  { field: 'parameterName', header: 'Parameter' },
  { field: 'synonym',       header: 'Synonym' },
  { field: 'matrix',        header: 'Matrix' },
  { field: 'test',          header: 'Test' },
  { field: 'methodName',    header: 'Method' },
  { field: 'components',    header: 'Component' },
];

// ── Add picker overlay — a full data grid (ALPACA Sample Sites lookup parity):
// shared AppDataGrid with row-selection checkboxes (per-row + select-all),
// column filters, pagination, and a global search box; OK adds every ticked row.
function PickerOverlay({ picker, rows, pickSearch, pickLoad, onSearch, selectedIds, onSelectionChange, onAddSelected, onClose }: {
  picker: 'site' | 'param';
  rows: (SiteRow | ParamRow)[];
  pickSearch: string; pickLoad: boolean;
  onSearch: (v: string) => void;
  selectedIds: Set<string | number>;
  onSelectionChange: (ids: Set<string | number>) => void;
  onAddSelected: () => void;
  onClose: () => void;
}) {
  const isSite = picker === 'site';
  // Bespoke picker popup keyboard: Esc closes (standing rule 14). Rendered only
  // while open, so the hook is always active here.
  const modalKeys = useModalKeys(true, { onClose });
  return (
    <div className="mt-overlay mt-overlay-2" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mt-picker mt-picker-grid" {...modalKeys}>
        <div className="mt-picker-hdr">
          <span>{isSite ? 'Sample Site' : 'Parameter'}</span>
          <button className="mt-x" onClick={onClose}>×</button>
        </div>
        <div className="mt-picker-search">
          <i className="ti ti-search" />
          <input autoFocus value={pickSearch} onChange={e => onSearch(e.target.value)} placeholder={`Search ${isSite ? 'sample locations' : 'parameters'}…`} />
        </div>
        <div className="mt-picker-gridbody">
          {isSite ? (
            <AppDataGrid<SiteRow>
              rows={rows as SiteRow[]} columns={SITE_PICKER_COLS} getRowId={r => r.oid}
              loading={pickLoad} selectable selectedIds={selectedIds} onSelectionChange={onSelectionChange}
              emptyText="No sample locations." />
          ) : (
            <AppDataGrid<ParamRow>
              rows={rows as ParamRow[]} columns={PARAM_PICKER_COLS} getRowId={r => r.oid}
              loading={pickLoad} selectable selectedIds={selectedIds} onSelectionChange={onSelectionChange}
              emptyText="No parameters." />
          )}
        </div>
        <div className="mt-picker-ftr">
          <span className="mt-picker-hint">{selectedIds.size} selected</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="mt-btn mt-btn-primary" disabled={selectedIds.size === 0} onClick={onAddSelected}>
              OK
            </button>
            <button className="mt-btn mt-btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Small presentational helpers ───────────────────────────────────────────────
function Field({ label, required, full, children }: { label: string; required?: boolean; full?: boolean; children: ReactNode }) {
  return (
    <div className={`mt-field${full ? ' mt-field-full' : ''}`}>
      {/* Required "*" as a .req span (not in the label text) so the shared
          side-label CSS renders "Label:*" — the ":" sits before the red * via
          .mt-field label .req::before (matches .sd-grid--side .sd-label). */}
      <label>{label}{required && <span className="req">*</span>}</label>
      {children}
    </div>
  );
}

interface ChildRow { key: string; cells: string[]; }
function ChildPanel({ title, count, editable, onAdd, onRemoveMany, head, rows, emptyText }: {
  title: string; count: number; editable: boolean;
  onAdd: () => void; onRemoveMany: (keys: string[]) => void;
  head: string[]; rows: ChildRow[]; emptyText: string;
}) {
  // Row selection for the ALPACA-style Add / Remove toolbar.
  const [sel, setSel] = useState<Set<string>>(new Set());
  // Client-side paging (owner request 2026-10-16, image 715): the full set stays in
  // `rows` — selection + the footer total span every page — and only the slice below
  // renders, under the shared <GridPager> footer that every standard grid uses.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const keys = rows.map(r => r.key);
  const selKeys = keys.filter(k => sel.has(k));
  const allChecked = rows.length > 0 && selKeys.length === rows.length;
  const someChecked = selKeys.length > 0 && !allChecked;
  const toggle = (k: string) =>
    setSel(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleAll = () => setSel(allChecked ? new Set() : new Set(keys));
  const doRemove = () => { onRemoveMany(selKeys); setSel(new Set()); };
  const colSpan = head.length + (editable ? 1 : 0);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);
  // An add/remove that shrinks the set past the current page falls back in range.
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  return (
    <div className="mt-child-block">
      {/* Title sits on the page, ABOVE the card (legacy DWQR / Task Checklist). */}
      <div className="mt-child-hdr">
        <span className="mt-child-title">{title}</span>
      </div>
      {/* ONE bordered card wraps the Add/Remove toolbar + grid + footer (owner
          request 2026-10-16, image 720 — the border encloses the whole control). */}
      <div className="mt-child">
        {editable && (
          <div className="mt-child-actions">
            <button className="mt-btn mt-btn-add" onClick={onAdd}><i className="ti ti-circle-plus" /> Add</button>
            <button className="mt-btn mt-btn-rm" disabled={selKeys.length === 0} onClick={doRemove}>
              <i className="ti ti-circle-minus" /> Remove{selKeys.length > 0 ? ` (${selKeys.length})` : ''}
            </button>
          </div>
        )}
      <div className="mt-child-scroll">
        {/* Column headers ALWAYS render (even with zero rows) — matches the
            legacy ALPACA grids, which show the header row above a centred
            "No data to display" message. */}
        <table className="mt-child-tbl">
          <thead>
            <tr>
              {editable && (
                <th className="mt-child-cb" style={{ width: 32 }}>
                  <input type="checkbox" checked={allChecked} disabled={rows.length === 0}
                    ref={el => { if (el) el.indeterminate = someChecked; }}
                    onChange={toggleAll} aria-label="Select all" />
                </th>
              )}
              {head.map(h => <th key={h}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="mt-child-empty" colSpan={colSpan}>{emptyText}</td>
              </tr>
            ) : (
              pagedRows.map(r => (
                <tr key={r.key} className={sel.has(r.key) ? 'mt-child-rowsel' : undefined}>
                  {editable && (
                    <td className="mt-child-cb">
                      <input type="checkbox" checked={sel.has(r.key)} onChange={() => toggle(r.key)}
                        aria-label="Select row" />
                    </td>
                  )}
                  {r.cells.map((c, i) => <td key={i} title={c}>{c}</td>)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Shared grid footer (owner request 2026-10-16, image 715) — Page X of N
          (N items) · prev / numbered / next · Page size — identical to every
          standard grid. Replaces the plain bottom-left count (it still shows the
          total); an empty grid shows no footer, matching the shared grid. */}
      <GridPager
        total={count}
        page={safePage}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />
      </div>
    </div>
  );
}

function apiErr(e: unknown, fallback: string): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? (e instanceof Error ? e.message : fallback);
}

// Error from a responseType:'blob' request — the error body is a Blob, so read
// + parse it for the API's { error: { message } } envelope (used by Test render).
async function blobErr(e: unknown, fallback: string): Promise<string> {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { error?: { message?: string } };
      if (parsed?.error?.message) return parsed.error.message;
    } catch { /* not JSON — fall through */ }
  }
  return apiErr(e, fallback);
}

// ── Scoped styles ───────────────────────────────────────────────────────────────
const MT_CSS = `
  .bl-tbl tbody tr { cursor: pointer; }
  .mt-link { color:var(--ai-blue); cursor:pointer; } .mt-link:hover { text-decoration:underline; }

  /* List-grid maximize (search-row Zoom) — page-level flex fill, keeps the search
     row (with Restore) visible; the breadcrumb + toolbar hide (owner request 2026-10-13). */
  .bl-page.mt-fullgrid { display:flex; flex-direction:column; height:100%; }
  .bl-page.mt-fullgrid .bl-top, .bl-page.mt-fullgrid .mt-max-hide { display:none !important; }
  .bl-page.mt-fullgrid .bl-grid-area { flex:1 1 0; min-height:0; display:flex; flex-direction:column; }
  .bl-page.mt-fullgrid .bl-grid-area .bl-card.bl-card-flex { flex:1 1 0; min-height:0; }
  /* Export + Maximize cluster on the search row (right-aligned), replacing the grid's own tools. */
  .mt-grid-acts { display:inline-flex; align-items:center; gap:4px; margin-left:auto; }

  .mt-sel-pill { display:inline-flex; align-items:center; gap:6px; background:color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 10%, #fff); color:var(--brand-accent-hover, var(--ai-orange-hover));
    border:1px solid var(--ai-required-line); padding:3px 4px 3px 10px; border-radius:12px; font-size:11px; font-weight:600; }
  .mt-sel-clear { background:transparent; border:none; color:var(--brand-accent-hover, var(--ai-orange-hover)); cursor:pointer; font-size:14px; line-height:1; padding:0 6px; }
  .mt-sel-clear:hover { color:var(--ai-orange-dark); }

  /* List/Detail toggle padding (matches ModuleListView / PaymentTermsSetup). */
  /* No extra left indent — .bl-grid-area already pads 20px, so the toggle sits
     flush with the card; +5px below it (owner request 2026-10-16). */
  .mt-viewtoggle { padding:10px 0 0; margin-bottom:5px; flex:0 0 auto; }
  /* Full-page detail (BO UI Standard §4 — the list is REPLACED, never a popup) */
  .mt-detail-hdr { padding:8px 14px; border-bottom:1px solid var(--ai-line); margin:0; background:var(--ai-header-bg);
    display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
  /* Detail navigator (title + breadcrumb) replacing the Audit chip (owner request 2026-10-16). */
  .mt-detail-nav { display:flex; flex-direction:column; gap:3px; }
  .mt-detail-bc { font-size:12px; }
  .mt-bc-link { cursor:pointer; }
  .mt-bc-link:hover { text-decoration:underline; }
  /* Action bar — rendered TOP and BOTTOM (identical set); Cancel/Close right. */
  .mt-actionbar { display:flex; align-items:center; justify-content:flex-end; gap:8px; flex-wrap:wrap; flex-shrink:0;
    padding:9px 14px; background:var(--ai-header-bg); border-bottom:1px solid var(--ai-line); }
  .mt-actionbar.bottom { border-bottom:0; border-top:1px solid var(--ai-line); }
  .mt-abtn-right { margin-left:auto; }
  .mt-detail-body { flex:1 1 auto; min-height:0; padding:0 0 18px; overflow:auto; }
  .mt-section-title { font-size:13px; font-weight:700; color:var(--ai-ink); padding:12px 16px 4px; }
  .mt-busy { display:flex; align-items:center; gap:8px; color:var(--ai-muted); font-size:13px; padding:24px; justify-content:center; }

  /* AI Standard side-label layout (owner request 2026-10-02): 2-column, label
     BESIDE its field, matching the legacy MOR Template Setup detail. */
  .mt-grid { display:grid; grid-template-columns:1fr 1fr; gap:6px 28px; padding:8px 16px 16px; }
  @media (max-width: 640px){ .mt-grid { grid-template-columns:1fr; } }
  /* AI Standard side-label layout (standing rule 12 — reference Settings → Inventory
     Management → Items): label BESIDE its field in a fixed 150px column; every control a
     uniform 26px; READ-ONLY grey. Owner request 2026-10-15: editable controls are PLAIN
     WHITE with SQUARE corners (the .sd-grid--square house look), not the yellow/rounded
     .sd-input; scoped to MOR via MT_CSS. */
  .mt-field { display:grid; grid-template-columns:var(--sd-hl-w, 150px) minmax(0,1fr); align-items:center; gap:8px; }
  .mt-field-full { grid-column:1 / -1; }
  /* Label: 12px/600/ink, LEFT-aligned, ellipsised — matches .sd-label. ":" appended
     after the caption, and BEFORE the required * (via .req::before), as in .sd-grid--side. */
  .mt-field label { font-size:12px; color:var(--ai-ink); font-weight:600; text-align:left;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .mt-field label:not(:empty):not(:has(.req))::after { content:":"; }
  .mt-field label .req { color:var(--ai-red); margin-left:2px; }
  .mt-field label .req::before { content:":"; color:var(--ai-ink); font-weight:normal; margin-right:1px; }
  /* Editable control = PLAIN WHITE, SQUARE corners, 26px (owner request 2026-10-15:
     "remove the yellow background ... square corners" — the .sd-grid--square house look). */
  .mt-field input, .mt-field select { font-size:13px; padding:var(--ai-control-pad-y, 4px) 10px;
    border:1px solid var(--ai-line); border-radius:0;
    height:var(--ai-control-h, 26px); min-height:var(--ai-control-h, 26px);
    width:100%; box-sizing:border-box; background:var(--ai-card, #fff); color:var(--ai-ink); outline:none; }
  .mt-field input:focus, .mt-field select:focus { border-color:var(--brand-accent, var(--ai-orange));
    box-shadow:0 0 0 2px rgba(232, 103, 10, 0.12); }
  /* Active checkbox: hug the LEFT of the field column (right next to the "Active"
     label) instead of being stretched to centre by the generic .mt-field input
     width:100% rule — the checkbox keeps its native size (user request 2026-10-02). */
  .mt-check { display:inline-flex; align-items:center; gap:8px; font-size:13px; color:var(--ai-ink);
    min-height:var(--ai-control-h, 26px); justify-self:start; }
  .mt-check input[type="checkbox"] { width:auto; flex:0 0 auto; margin:0; }
  /* View-mode value = a read-only BOXED field (matches .sd-input.readonly: grey fill,
     muted text, 26px). min-height (not fixed height) so a long value can still grow
     rather than clip in view mode. */
  .mt-val { font-size:13px; padding:var(--ai-control-pad-y, 4px) 10px; color:var(--ai-muted);
    border:1px solid var(--ai-line); border-radius:0; background:var(--ai-readonly-bg);
    min-height:var(--ai-control-h, 26px); box-sizing:border-box; display:flex; align-items:center; }
  .mt-val strong { color:var(--ai-ink); }
  /* Category combo: 26px, plain white, SQUARE corners to match the other editable
     controls (owner request 2026-10-15; scoped to MOR, the shared AiCombo is unchanged). */
  .mt-cat-combo.ai-lkbox { height:var(--ai-control-h, 26px); min-height:var(--ai-control-h, 26px); border-radius:0; }
  .mt-cat-combo .aic-trigger { height:var(--ai-control-h, 26px); min-height:var(--ai-control-h, 26px); background:var(--ai-card, #fff); }
  /* Category ＋: green "add" glyph on the field's own white background (not a solid
     orange box) — the inside-combo "+" style the other forms use (owner request
     2026-10-13, image 513). Scoped to MOR so the shared AiCombo is unchanged elsewhere. */
  .mt-cat-combo .aic-new { background:transparent; border-left:1px solid var(--ai-line); color:var(--ai-icon-new, #1f9159); }
  .mt-cat-combo .aic-new:hover:not(:disabled) { background:var(--ai-row-hover, #f1f3f7); color:var(--ai-icon-new, #1f9159); }
  /* Caret [▼]: neutral white box + grey triangle, so the Category control carries NO
     coloured background and matches the other plain white fields (owner request
     2026-10-15: "remove the category input field control background color"). The default
     AiCombo caret is orange; scoped to MOR, the shared combo is unchanged elsewhere. */
  .mt-cat-combo .aic-caret { background:var(--ai-card, #fff); border-left:1px solid var(--ai-line); color:var(--ai-muted); }
  .mt-cat-combo .aic-caret:hover:not(:disabled) { background:var(--ai-row-hover, #f1f3f7); color:var(--ai-muted); }
  .mt-cat-combo .aic-caret-tri { border-top-color:var(--ai-muted); }

  /* Stacked full-width (legacy layout): Sample Locations above Parameters, each
     scrolling horizontally so the full column set is reachable. */
  .mt-children { display:flex; flex-direction:column; gap:16px; padding:0 16px; }
  .mt-child { border:1px solid var(--ai-line); border-radius:8px; overflow:hidden; display:flex; flex-direction:column; }
  /* Section TITLE sits on the page, above the card (owner request 2026-10-16, legacy
     DWQR parity). The Add/Remove toolbar is now INSIDE the card (see .mt-child-actions)
     so ONE border wraps the toolbar + grid + footer, matching the legacy Task Checklist
     (owner request 2026-10-16, image 720). */
  .mt-child-block { display:flex; flex-direction:column; gap:6px; }
  .mt-child-hdr { display:flex; align-items:center; padding:0; background:transparent; border-bottom:none; }
  .mt-child-title { font-size:14px; font-weight:700; color:var(--ai-ink); }
  .mt-child-scroll { max-height:260px; overflow:auto; }
  /* Footer row count — bottom-left, matching the shared grid footer (replaces the
     header badge; owner request 2026-10-13). */
  .mt-child-foot { padding:7px 12px; font-size:12px; color:var(--ai-muted); background:var(--ai-header-bg);
    border-top:1px solid var(--ai-line); text-align:left; }
  .mt-child-empty { padding:14px; font-size:12px; color:var(--ai-muted); font-style:italic; text-align:center; }
  .mt-child-tbl { width:100%; border-collapse:collapse; font-size:12px; }
  /* Table metrics — matched to the shared <AppDataGrid> compact Settings grid (owner
     request 2026-10-16, "set the grid setting for these grids"): 22px data rows
     (2px + 18px line) and a 12.5px / 700 header, like every other Settings grid now
     is under the shell.css grp-settings scope; no zebra. */
  .mt-child-tbl th { text-align:left; padding:6px 12px; background:var(--ai-header-bg); color:var(--ai-ink);
    font-size:12.5px; font-weight:700; position:sticky; top:0;
    border-bottom:1px solid var(--ai-line); border-right:1px solid var(--ai-line-soft); }
  .mt-child-tbl td { font-size:12.5px; padding:2px 12px; line-height:18px; border-bottom:1px solid var(--ai-line-soft);
    border-right:1px solid var(--ai-line-soft); color:var(--ai-ink); max-width:200px;
    overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .mt-child-tbl tbody tr:hover td { background:var(--ai-row-hover); }
  .mt-child-rm button { background:transparent; border:none; color:var(--ai-red); cursor:pointer; font-size:14px; padding:2px 4px; }
  .mt-child-rm button:hover { color:var(--ai-red); }
  /* Add/Remove toolbar — now the TOP ROW INSIDE the bordered card (image 720),
     left-aligned, with a separator above the grid header. */
  .mt-child-actions { display:flex; gap:8px; padding:7px 10px; border-bottom:1px solid var(--ai-line); }
  .mt-child-cb { width:32px; text-align:center; }
  .mt-child-cb input { cursor:pointer; }
  .mt-child-rowsel { background:var(--ai-row-selected); }
  /* Compact pager buttons for the child-grid footers ONLY (owner request
     2026-10-16, image 716 — "reduce … only size"): smaller page-number buttons to
     suit the 22px rows. Scoped to .mt-child so the shared <GridPager> is unchanged
     on every other grid. SIZE ONLY — colours, borders and the orange active state
     stay the shared pager's. */
  .mt-child .adg-pg-btn { min-width:22px; height:20px; padding:0 4px; font-size:11px; }
  .mt-child .adg-pg-btn i { font-size:11px; }
  .mt-child .adg-pg-ellipsis { font-size:11px; padding:0 3px; }

  .mt-btn { padding:6px 14px; font-size:12px; font-weight:600; border-radius:4px; cursor:pointer; border:1px solid transparent;
    display:inline-flex; align-items:center; gap:5px; }
  .mt-btn-primary { background:var(--brand-accent, var(--ai-orange)); color:#fff; }
  .mt-btn-primary:hover { background:var(--brand-accent-hover, var(--ai-orange-hover)); }
  .mt-btn-primary:disabled { opacity:.6; cursor:not-allowed; }
  .mt-btn-secondary { background:transparent; color:var(--ai-ink); border-color:transparent; }
  .mt-btn-secondary:hover { background:var(--ai-line-soft); border-color:var(--ai-line); }
  /* The Test (Date Range) dialog's Cancel keeps a visible border at rest (owner
     request 2026-10-16, image 725). Scoped so other secondary buttons are unchanged. */
  .mt-test-ftr .mt-btn-secondary { border-color:var(--ai-line); }
  /* Add/Remove = plain toolbar buttons, green "+" / grey "−" (legacy DWQR look,
     owner request 2026-10-15) — no card-button chrome. */
  .mt-btn-add, .mt-btn-rm { background:transparent; border:none; border-radius:0; padding:2px 2px; font-size:12px;
    font-weight:600; display:inline-flex; align-items:center; gap:5px; cursor:pointer; }
  .mt-btn-add { color:var(--ai-ink); }
  .mt-btn-add .ti { color:var(--ai-icon-new, #1f9159); font-size:16px; }
  .mt-btn-add:hover:not(:disabled) { color:var(--ai-icon-new, #1f9159); background:transparent; }
  .mt-btn-rm { color:var(--ai-muted); }
  .mt-btn-rm .ti { color:var(--ai-muted); font-size:16px; }
  .mt-btn-rm:hover:not(:disabled) { color:var(--ai-ink); background:transparent; }
  .mt-btn-rm:disabled { color:var(--ai-muted); opacity:.55; cursor:not-allowed; }

  .mt-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center;
    justify-content:center; z-index:9000; backdrop-filter:blur(2px); }
  .mt-overlay-2 { z-index:9100; }
  .mt-x { width:26px; height:26px; border:none; border-radius:4px; background:rgba(255,255,255,.18); color:#fff;
    cursor:pointer; font-size:17px; }
  .mt-x:hover { background:rgba(255,255,255,.32); }

  .mt-picker { width:min(94vw, 520px); max-height:80vh; background:#fff; border-radius:10px; overflow:hidden;
    display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.45); }
  /* Grid-style picker (ALPACA Sample Sites lookup) — wider, grid fills the body. */
  .mt-picker-grid { width:min(96vw, 1180px); height:86vh; max-height:86vh; }
  .mt-picker-gridbody { flex:1; min-height:0; overflow:hidden; display:flex; flex-direction:column; padding:8px 12px; }
  .mt-picker-gridbody > * { flex:1; min-height:0; }
  /* Header: brand orange to match the forms (owner request 2026-10-15), not blue. */
  .mt-picker-hdr { display:flex; align-items:center; justify-content:space-between; padding:10px 16px; background:var(--brand-accent, var(--ai-orange));
    color:#fff; font-size:13px; font-weight:600; }
  .mt-picker-search { display:flex; align-items:center; gap:6px; padding:8px 12px; border-bottom:1px solid var(--ai-line-soft); }
  .mt-picker-search i { color:var(--ai-muted); }
  .mt-picker-search input { flex:1; border:1px solid var(--ai-line); border-radius:4px; padding:6px 8px; font-size:13px; outline:none; }
  .mt-picker-search input:focus { border-color:var(--brand-accent, var(--ai-orange)); }
  .mt-picker-list { flex:1; overflow:auto; min-height:120px; }
  .mt-picker-empty { padding:18px; text-align:center; color:var(--ai-muted); font-size:12px; font-style:italic; }
  .mt-picker-row { width:100%; text-align:left; background:#fff; border:none; border-bottom:1px solid var(--ai-line-soft);
    padding:8px 14px; cursor:pointer; display:flex; flex-direction:column; gap:1px; position:relative; }
  .mt-picker-row:hover:not(:disabled) { background:var(--ai-row-hover); }
  .mt-picker-row:disabled { opacity:.55; cursor:default; }
  .mt-picker-main { font-size:13px; color:var(--ai-ink); font-weight:600; }
  .mt-picker-sub { font-size:11px; color:var(--ai-muted); }
  .mt-picker-added { position:absolute; right:12px; top:50%; transform:translateY(-50%); color:var(--ai-green); }
  .mt-picker-ftr { display:flex; align-items:center; justify-content:space-between; padding:8px 14px; background:var(--ai-header-bg); border-top:1px solid var(--ai-line-soft); }
  .mt-picker-hint { font-size:11px; color:var(--ai-muted); }

  .mt-test { width:min(94vw, 460px); background:#fff; border-radius:10px; overflow:hidden;
    display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.45); }
  /* Header: brand orange to match the forms (owner request 2026-10-16), not blue. */
  .mt-test-hdr { display:flex; align-items:center; justify-content:space-between; padding:10px 16px; background:var(--brand-accent, var(--ai-orange)); color:#fff; font-size:13px; font-weight:600; }
  .mt-test-body { padding:14px 16px; display:flex; flex-direction:column; gap:12px; }
  .mt-test-row { display:flex; flex-direction:column; gap:10px; }
  /* Label BESIDE the input (owner request 2026-10-16, image 728) — label-left,
     input-right, both rows aligned by a fixed-width label column. */
  .mt-test-row label { display:flex; flex-direction:row; align-items:center; gap:10px; }
  .mt-test-lbl { flex:0 0 68px; font-size:12px; font-weight:600; color:var(--ai-ink); }
  .mt-test-row input { flex:1 1 auto; font-size:13px; padding:6px 8px; border:1px solid var(--ai-line); border-radius:4px; outline:none; }
  .mt-test-row input:focus { border-color:var(--brand-accent, var(--ai-orange)); }
  .mt-test-ftr { display:flex; justify-content:flex-end; gap:8px; padding:10px 16px; background:var(--ai-header-bg); border-top:1px solid var(--ai-line-soft); }
`;
